'use client'

import type { ReactNode } from 'react'
import { useState } from 'react'

import { Dialog, DialogContent } from '@ui/web/components'

import { SingleForm } from './single-form'

export * from './consent-field'
export * from './single-form'
export * from './use-enroll-children'

export interface EnrollChildDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** A family enrols an "anak", a school a "siswa" — drives copy and the consent declaration. */
  type: 'web' | 'school'
  /** School-only fields (Kelas, Tingkat), rendered under Tahun lahir. */
  renderExtraFields?: (args: { disabled: boolean }) => ReactNode
  /** Values those extra fields collected, passed straight through to `signUpChild`. */
  enrollOptions?: { teamId?: string | null; schoolClass?: string | null }
  /** Bulk enrolment, when the host has one. Omit and the footer offers plain Batal instead. */
  renderBulkForm?: (args: { onSwitchToSingle: () => void; onClose: () => void }) => ReactNode
  /** Called after the dialog closes, so the host can reset whatever it hoisted. */
  onClosed?: () => void
}

/**
 * Creates one child account: avatar, name, nickname, birth year, and a consent declaration.
 *
 * Shared because a family and a school do the same thing here. What differs rides on `type` (copy
 * and the declaration) or on slots: a school also picks a class and a grade, and can switch into
 * bulk enrolment, neither of which a family has.
 */
export function EnrollChildDialog({
  open,
  onOpenChange,
  type,
  renderExtraFields,
  enrollOptions,
  renderBulkForm,
  onClosed,
}: EnrollChildDialogProps) {
  const [mode, setMode] = useState<'single' | 'bulk'>('single')
  const [consented, setConsented] = useState(false)

  /**
   * Reset lives here rather than in `onOpenChange`: writing the controlled `open` prop never
   * invokes `onOpenChange`, so the forms' success path — which calls `close` directly — would
   * otherwise leave the consent box ticked for the next child.
   */
  const close = () => {
    onOpenChange(false)
    setMode('single')
    setConsented(false)
    onClosed?.()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          onOpenChange(true)
          return
        }
        close()
      }}
    >
      <DialogContent className='srd:w-full srd:max-w-lg'>
        {mode === 'single' || !renderBulkForm ? (
          <SingleForm
            type={type}
            consented={consented}
            onConsentChange={setConsented}
            renderExtraFields={renderExtraFields}
            enrollOptions={enrollOptions}
            onSwitchToBulk={renderBulkForm ? () => setMode('bulk') : undefined}
            onClose={close}
          />
        ) : (
          renderBulkForm({ onSwitchToSingle: () => setMode('single'), onClose: close })
        )}
      </DialogContent>
    </Dialog>
  )
}
